/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { IS_SSR } from '~/utils/client-only'
import { logger } from '~/utils/sentry/client-only-logger'

import type { BroadcastMessage } from '~/types/broadcast-message.model'

type JsonData = string | number | null | { [key: string]: JsonData } | object | boolean | JsonData[]

type UniqueLocalStorageKey = { uniqueKey: number }

// Message can be any json data
export type Message = BroadcastMessage & Record<string, JsonData>

export type Event<Msg extends Message> = { data: Msg }

// key exists to make value unique, even if the same data is broadcasted
type LocalStorageEvent<Msg extends Message> = Event<Msg> & UniqueLocalStorageKey

type OnMessage<Msg extends Message> = (
  // here we 100% get Event<Msg>, rest is optional, because it could be a polyfill/BroadcastChannel
  event: Event<Msg> & Partial<MessageEvent> & Partial<UniqueLocalStorageKey>,
) => void

type TabBroadcastChannel<Msg extends Message> = {
  postMessage: (msg: Msg) => void
  onmessage: OnMessage<Msg> | null
}

const isBroadcastChannelSupported = typeof BroadcastChannel !== 'undefined'

/**
 * This is a wrapper around BroadcastChannel which fallbacks to local storage,
 * when BroadcastChannel is not supported. It supports basic BroadcastChannel API,
 * but the value, passed to `.postMessage` should not fail in `JSON.parse(JSON.stringify(value))`
 * That is for localStorage polyfill to work correctly.
 * ```ts
 * // In Browser TAB 1 //
 * const broadcastChannel = useTabBroadcastChannel('some-channel-name')
 *
 * onMounted(() => {
 *   broadcastChannel.postMessage('hi from TAB 1')
 *   // or .postMessage({ some: 'message'}), .postMessage(1), .postMessage(null)
 *   // or any other value, that is okay for JSON.parse(JSON.stringify(value))
 * })
 *
 * // In Browser TAB 2 //
 * const broadcastChannel = useTabBroadcastChannel('some-channel-name')
 * onMounted(() => {
 *   // this tab will log any message, posted in Browser TAB 1
 *   // in our example, TAB 2 will console.log: { data: "hi from TAB 1" }
 *   broadcastChannel.onmessage = console.log
 * })
 *
 * // By default, the emitter Tab does not receive the event (only other tabs)
 * // If you want the message to be received by the same tab that emitted it
 * // you can create 2 instances of broadcastChannels with the same name
 * // ATTENTION: might not work in old browsers in polyfilled version (localStorage events)
 * const broadcastChannel = useTabBroadcastChannel('unidirectional-channel')
 * const receiverChannel = useTabBroadcastChannel('unidirectional-channel')
 *
 * receiverChannel.onmessage = console.log
 * broadcastChannel.postMessage('Hi to all the tabs!')
 * // all tabs will console.log: { data: "Hi to all the tabs!"}
 * ```
 */
export const useTabBroadcastChannel = <Msg extends Message = Message>(
  channelName: string,
): TabBroadcastChannel<Msg> => {
  const broadcastChannel = isBroadcastChannelSupported
    ? new BroadcastChannel(channelName)
    : undefined

  const onMessage = ref<OnMessage<Msg>>(doNothing)

  const handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== channelName || IS_SSR) {
      return
    }

    try {
      if (!event.newValue) {
        throw new Error(`Incorrect value: ${String(event.newValue)}`)
      }
      const parsedMsg: unknown = JSON.parse(event.newValue)

      if (
        !parsedMsg ||
        typeof parsedMsg !== 'object' ||
        !('data' in parsedMsg)
      ) {
        throw new Error(`Incorrect value`)
      }
      // yes, possibly anything was sent here
      onMessage.value(parsedMsg as LocalStorageEvent<Msg>)
    } catch (error) {
      const formatError = (e: unknown) =>
        new Error('Something went wrong when parsing StorageEvent', {
          cause: e,
        })

      logger.error(error, { formatError })
    }
  }

  onMounted(() => {
    if (IS_SSR) {
      return
    }
    if (broadcastChannel) {
      broadcastChannel.onmessage = (data) => {
        onMessage.value(data)
      }
      return
    }

    window.addEventListener('storage', handleStorageEvent)
  })

  onUnmounted(() => {
    if (IS_SSR) {
      return
    }
    window.removeEventListener('storage', handleStorageEvent)
    broadcastChannel?.close()
  })

  return {
    postMessage(data: Msg) {
      if (IS_SSR) {
        return
      }
      if (broadcastChannel) {
        broadcastChannel.postMessage(data)
        return
      }

      const localStorageEvent: LocalStorageEvent<Msg> = {
        data,
        uniqueKey: Math.random(),
      }

      window.localStorage.setItem(
        channelName,
        JSON.stringify(localStorageEvent),
      )
    },

    set onmessage(cb: OnMessage<Msg>) {
      onMessage.value = cb
    },
    get onmessage() {
      return onMessage.value
    },
  }
}
